import {
  memo,
  cloneElement,
  useMemo,
  useState,
  useEffect,
  useCallback,
  Children,
  isValidElement,
  Fragment,
} from 'react';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import CategoryMenuBar from '@wconcept/wconcept-ui-renewal/components/organisms/_menuBar/CategoryMenuBar';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

/** setTimeout이 다룰 수 있는 최대 지연(32bit). 넘기면 즉시 실행돼 버린다. */
const MAX_TIMEOUT_DELAY = 2 ** 31 - 1;

interface IProps {
  brandMainTitle?: string;
  brandMainSubTitle?: string;
  brandSubTitle?: string;
  brandSubSubTitle?: string;
  tabMenuList?: React.ReactNode;
  selectedTabId?: string;
  categoryTabList?: React.ReactNode[];
  selectedCateTabId?: string;
  /** 1: 마감 | 2: 진행중 | 3: 진행 예정(미도래) */
  status?: '1' | '2' | '3' | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  serverTime?: string | Date | undefined;
  brandMain?: React.JSX.Element;
  brandSub?: React.JSX.Element;
  colorType?: ColorType;
  isDesktop?: boolean;
}

const BrandWrapSection2 = ({
  brandMainTitle = '놓치면 안 될 브랜드',
  brandMainSubTitle,
  brandSubTitle,
  brandSubSubTitle,
  tabMenuList,
  selectedTabId,
  categoryTabList,
  selectedCateTabId,
  status,
  startDate = '',
  endDate = '',
  serverTime,
  brandMain,
  brandSub,
  colorType = 'white',
  isDesktop,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const [eventStatus, setEventStatus] = useState<'1' | '2' | '3'>(
    status || '2'
  );
  const [serverTimeOffset, setServerTimeOffset] = useState<number | null>(null);

  const cName = classNameBind([
    'brand-section',
    !isCustomColor ? `bg-type-${colorType}` : '',
    `event-status-${eventStatus}`,
  ]);

  useEffect(() => {
    if (serverTime) {
      setServerTimeOffset(new Date(serverTime).getTime() - Date.now());
    } else {
      setServerTimeOffset(0);
    }
  }, [serverTime]);

  const getAdjustedTime = useCallback(
    () => new Date(Date.now() + (serverTimeOffset ?? 0)),
    [serverTimeOffset]
  );

  // 행사 상태(마감/진행중/미도래) 계산. 미도래(3)일 때 상품·쿠폰·자세히보기 딤/비노출 처리에 사용된다.
  // 상태가 다음으로 바뀌기까지 남은 ms를 반환한다(더 이상 바뀌지 않으면 null).
  const updateEventStatus = useCallback((): number | null => {
    if (serverTimeOffset === null) return null;

    // 날짜가 없으면 외부에서 내려준 status를 그대로 따른다.
    if (!startDate || !endDate) {
      setEventStatus(status || '2');
      return null;
    }

    const now = getAdjustedTime().getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (now < start) {
      setEventStatus('3');
      return start - now;
    }
    if (now < end) {
      setEventStatus('2');
      return end - now;
    }
    setEventStatus('1');
    return null;
  }, [startDate, endDate, status, getAdjustedTime, serverTimeOffset]);

  // 브랜드 섹션에 행사 상태와 컬러를 주입한다.
  // 사용처에서 <>…</> 로 섹션을 여러 개 넘기는 경우가 있는데
  // Fragment에 cloneElement 하면 key/children 외 props가 무시되므로 자식 각각에 주입한다.
  const injectSectionProps = useCallback(
    (node: React.JSX.Element | undefined): React.ReactNode => {
      if (!node) return null;

      const injectProps = { status: eventStatus, colorType };

      if (node.type === Fragment) {
        const { children } = node.props as { children?: React.ReactNode };

        return cloneElement(
          node,
          undefined,
          Children.map(children, child =>
            isValidElement<Record<string, unknown>>(child)
              ? cloneElement(child, injectProps)
              : child
          )
        );
      }

      return cloneElement(node, injectProps);
    },
    [eventStatus, colorType]
  );

  // 카운트다운 표시가 없는 UI라 매초 폴링할 이유가 없다.
  // 다음 상태 전환 시점에만 한 번 깨우고, 그 뒤 다시 예약한다.
  useEffect(() => {
    if (serverTimeOffset === null) return undefined;

    let timerId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      const remain = updateEventStatus();
      if (remain === null) return;
      // setTimeout 최대 지연(약 24.8일)을 넘으면 잘라서 재예약한다.
      const delay = Math.min(remain + 50, MAX_TIMEOUT_DELAY);
      timerId = setTimeout(scheduleNext, delay);
    };

    scheduleNext();
    return () => clearTimeout(timerId);
  }, [updateEventStatus, serverTimeOffset]);

  const tabSelectedIndex = useMemo(() => {
    if (!Array.isArray(tabMenuList) || selectedTabId === undefined)
      return undefined;
    const idx = (
      tabMenuList as { props?: { value?: string | number } }[]
    ).findIndex(
      item =>
        item?.props?.value != null &&
        String(item.props.value) === String(selectedTabId)
    );
    return idx === -1 ? undefined : idx;
  }, [tabMenuList, selectedTabId]);

  const renderTabMenu = () => {
    if (!tabMenuList) return null;

    return (
      <S.TabMenu className="date-tab">
        {isDesktop ? (
          <div className="tab-list">{tabMenuList}</div>
        ) : (
          <ItemsScrollBar
            perView={0}
            colGap={6}
            className="tab-list"
            selectedIndex={tabSelectedIndex}
          >
            {tabMenuList}
          </ItemsScrollBar>
        )}
      </S.TabMenu>
    );
  };

  const renderCategoryTabMenu = () => {
    if (!Array.isArray(categoryTabList) || categoryTabList.length === 0) {
      return null;
    }

    return isDesktop ? (
      <div className="category-tab">{categoryTabList}</div>
    ) : (
      <CategoryMenuBar
        type="type6"
        items={categoryTabList}
        selectedValue={selectedCateTabId}
        className="category-tab"
      />
    );
  };

  return (
    <S.BrandWrapSection2
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
      $pointColor={customColors?.pointColor}
      $emphasisTextColor={customColors?.emphasisTextColor}
    >
      {/* 메인 타이틀 */}
      {brandMainTitle && (
        <Text
          block
          className="content-title"
          weight={600}
          size={18}
          lineHeight="130%"
          color={customColors?.textColor}
        >
          {brandMainTitle}
        </Text>
      )}
      {brandMainSubTitle && (
        <Text
          className="content-sub-title"
          size={13}
          lineHeight={15}
          color={customColors?.textColor ?? '#000'}
          block
        >
          {brandMainSubTitle}
        </Text>
      )}
      {tabMenuList && renderTabMenu()}
      {categoryTabList && renderCategoryTabMenu()}
      {injectSectionProps(brandMain)}

      {/* 서브 타이틀 */}
      {brandSubTitle && (
        <Text
          block
          className="content-title"
          weight={600}
          size={18}
          lineHeight="130%"
          color={customColors?.textColor}
        >
          {brandSubTitle}
        </Text>
      )}
      {brandSubSubTitle && (
        <Text
          className="content-sub-title"
          size={13}
          lineHeight={15}
          color={customColors?.textColor ?? '#000'}
          block
        >
          {brandSubSubTitle}
        </Text>
      )}
      {injectSectionProps(brandSub)}
    </S.BrandWrapSection2>
  );
};

export default memo(BrandWrapSection2);
